import './app.css'
import * as Sentry from '@sentry/vue'
import { createApp } from 'vue'
import App from './components/App.vue'
import router from './router'
import i18n from './i18n'
import client from './api/client'
import { useAdminAuth } from './composables/useAdminAuth'
import { scrubEvent } from './lib/sentry'

// Redirect to the admin login page on 401 when the token expires mid-session.
client.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (
      typeof error === 'object' &&
      error !== null &&
      'response' in error &&
      (error as { response: { status: number } }).response?.status === 401
    ) {
      const { superAdmin } = useAdminAuth()
      localStorage.removeItem('startline_admin_token')
      superAdmin.value = null
      void router.push({ name: 'admin.login' })
    }
    return Promise.reject(error)
  },
)

const app = createApp(App)

Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN as string | undefined,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined,
  release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
  // No-op when DSN is absent (local dev / CI without secrets).
  enabled: Boolean(import.meta.env.VITE_SENTRY_DSN),
  // Hard requirement per spec §4: never send cookies, IP, headers, or user fields.
  sendDefaultPii: false,
  integrations: [
    Sentry.browserTracingIntegration({ router }),
  ],
  // Free-tier safe: tracing off by default. Raise temporarily for latency work.
  tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0),
  // Session Replay intentionally NOT included — quota + PII risk.
  // Critical: the bearer interceptor in apps/admin/src/api/client.ts attaches
  // Authorization: Bearer <startline_admin_token> to every request. Sentry must
  // never capture this header. scrubEvent removes it from every event payload.
  beforeSend: scrubEvent,
})

app.use(router).use(i18n).mount('#app')
